import { useState, useCallback } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Tabs, Tab, Select, MenuItem, FormControl,
  Alert, Collapse,
} from '@mui/material'
import { Add, Edit, Delete, ChevronRight, ExpandMore, Save } from '@mui/icons-material'
import { useApi, apiPost, apiPut, apiDelete, fetchObjects, type CRMObjectRow } from '../../api/client'
import { fetchObjectSchema } from '../../api/client'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'
import {
  fetchObjectPermissions, updateObjectPermissions,
  fetchFieldPermissions, updateFieldPermissions,
  fetchDataScopes, updateDataScopes,
} from '../../api/roles'
import type { ApiResponse, ObjectPermission, FieldPermission, DataScopeType, FieldMeta } from '@crm/shared'

interface RoleRow {
  id: string
  name: string
  description: string | null
  is_system: number
}

const OBJECT_PERMISSION_OPTIONS: { value: ObjectPermission; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'read', label: 'Read' },
  { value: 'read_write', label: 'Read/Write' },
  { value: 'full', label: 'Full' },
]

const FIELD_PERMISSION_OPTIONS: { value: FieldPermission; label: string }[] = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'readonly', label: 'Read Only' },
  { value: 'read_write', label: 'Read/Write' },
]

const SCOPE_TYPE_OPTIONS: { value: DataScopeType; label: string }[] = [
  { value: 'all', label: 'All Records' },
  { value: 'owned', label: 'Owned by Me' },
  { value: 'team', label: 'Team' },
  { value: 'custom', label: 'Custom Filter' },
]

export function RoleManagement() {
  const { data, mutate } = useApi<ApiResponse<RoleRow[]>>('/roles')
  const roles = data?.data ?? []
  const toast = useToastStore()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editRole, setEditRole] = useState<RoleRow | null>(null)
  const [form, setForm] = useState({ name: '', description: '' })
  const [saving, setSaving] = useState(false)

  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null)
  const [permTab, setPermTab] = useState(0)
  const [objectsList, setObjectsList] = useState<CRMObjectRow[]>([])
  const [objectsFieldsMap, setObjectsFieldsMap] = useState<Map<string, FieldMeta[]>>(new Map())

  const [objPerms, setObjPerms] = useState<Map<string, ObjectPermission>>(new Map())
  const [fieldPerms, setFieldPerms] = useState<Map<string, FieldPermission>>(new Map())
  const [dataScopes, setDataScopes] = useState<Map<string, { scope_type: DataScopeType; custom_filter: string }>>(new Map())

  const [permLoading, setPermLoading] = useState(false)
  const [permSaving, setPermSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [expandedFieldObjIds, setExpandedFieldObjIds] = useState<Set<string>>(new Set())

  const toggleFieldObj = useCallback((objectId: string) => {
    setExpandedFieldObjIds((prev) => {
      const next = new Set(prev)
      if (next.has(objectId)) {
        next.delete(objectId)
      } else {
        next.add(objectId)
      }
      return next
    })
  }, [])

  const openCreate = () => {
    setEditRole(null)
    setForm({ name: '', description: '' })
    setDialogOpen(true)
  }

  const openEdit = (role: RoleRow) => {
    setEditRole(role)
    setForm({ name: role.name, description: role.description ?? '' })
    setDialogOpen(true)
  }

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (editRole) {
        await apiPut(`/roles/${editRole.id}`, form)
        toast.success('角色已更新')
      } else {
        await apiPost('/roles', form)
        toast.success('角色已创建')
      }
      mutate()
      setDialogOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [editRole, form, mutate, toast])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await apiDelete(`/roles/${id}`)
      mutate()
      if (expandedRoleId === id) setExpandedRoleId(null)
      toast.success('角色已删除')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败')
    }
    setDeleteConfirm(null)
  }, [mutate, expandedRoleId])

  const toggleExpand = useCallback(async (roleId: string) => {
    if (expandedRoleId === roleId) {
      setExpandedRoleId(null)
      return
    }
    setExpandedRoleId(roleId)
    setPermTab(0)
    setPermLoading(true)
    try {
      const [objectsRes, objPermRes, fieldPermRes, scopeRes] = await Promise.all([
        fetchObjects(),
        fetchObjectPermissions(roleId),
        fetchFieldPermissions(roleId),
        fetchDataScopes(roleId),
      ])

      const objs = objectsRes.data ?? []
      setObjectsList(objs)

      const objPermMap = new Map<string, ObjectPermission>()
      for (const p of objPermRes.data ?? []) {
        objPermMap.set(p.object_id, p.permission)
      }
      setObjPerms(objPermMap)

      const fieldPermMap = new Map<string, FieldPermission>()
      for (const p of fieldPermRes.data ?? []) {
        fieldPermMap.set(`${p.object_id}:${p.field_id}`, p.permission)
      }
      setFieldPerms(fieldPermMap)

      const scopeMap = new Map<string, { scope_type: DataScopeType; custom_filter: string }>()
      for (const s of scopeRes.data ?? []) {
        scopeMap.set(s.object_id, { scope_type: s.scope_type, custom_filter: s.custom_filter ?? '' })
      }
      setDataScopes(scopeMap)

      const fieldsMap = new Map<string, FieldMeta[]>()
      await Promise.all(objs.map(async (obj) => {
        try {
          const schema = await fetchObjectSchema(obj.id)
          fieldsMap.set(obj.id, schema.data?.fields ?? [])
        } catch {
          fieldsMap.set(obj.id, [])
        }
      }))
      setObjectsFieldsMap(fieldsMap)
    } catch {
      setObjectsList([])
    } finally {
      setPermLoading(false)
    }
  }, [expandedRoleId])

  const handleObjPermChange = (objectId: string, permission: ObjectPermission) => {
    setObjPerms((prev) => {
      const next = new Map(prev)
      if (permission === 'none') {
        next.delete(objectId)
      } else {
        next.set(objectId, permission)
      }
      return next
    })
  }

  const handleFieldPermChange = (objectId: string, fieldId: string, permission: FieldPermission) => {
    setFieldPerms((prev) => {
      const next = new Map(prev)
      next.set(`${objectId}:${fieldId}`, permission)
      return next
    })
  }

  const handleScopeChange = (objectId: string, scopeType: DataScopeType) => {
    setDataScopes((prev) => {
      const next = new Map(prev)
      next.set(objectId, { scope_type: scopeType, custom_filter: prev.get(objectId)?.custom_filter ?? '' })
      return next
    })
  }

  const handleCustomFilterChange = (objectId: string, filter: string) => {
    setDataScopes((prev) => {
      const next = new Map(prev)
      next.set(objectId, { scope_type: 'custom', custom_filter: filter })
      return next
    })
  }

  const saveObjectPermissions = useCallback(async () => {
    if (!expandedRoleId) return
    setPermSaving(true)
    try {
      const permissions = Array.from(objPerms.entries()).map(([object_id, permission]) => ({ object_id, permission }))
      await updateObjectPermissions(expandedRoleId, permissions)
      toast.success('对象权限已保存')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setPermSaving(false)
    }
  }, [expandedRoleId, objPerms])

  const saveFieldPermissions = useCallback(async () => {
    if (!expandedRoleId) return
    setPermSaving(true)
    try {
      const permissions: Array<{ object_id: string; field_id: string; permission: FieldPermission }> = []
      for (const [key, permission] of fieldPerms.entries()) {
        const [object_id, field_id] = key.split(':')
        permissions.push({ object_id, field_id, permission })
      }
      await updateFieldPermissions(expandedRoleId, permissions)
      toast.success('字段权限已保存')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setPermSaving(false)
    }
  }, [expandedRoleId, fieldPerms])

  const saveDataScopes = useCallback(async () => {
    if (!expandedRoleId) return
    setPermSaving(true)
    try {
      const scopes = Array.from(dataScopes.entries()).map(([object_id, val]) => ({
        object_id,
        scope_type: val.scope_type,
        custom_filter: val.scope_type === 'custom' ? val.custom_filter : undefined,
      }))
      await updateDataScopes(expandedRoleId, scopes)
      toast.success('数据范围已保存')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setPermSaving(false)
    }
  }, [expandedRoleId, dataScopes])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Roles</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreate}>New Role</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 40 }} />
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100, whiteSpace: 'nowrap' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <>
                <TableRow key={role.id} hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleExpand(role.id)}>
                      {expandedRoleId === role.id ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.description ?? '—'}</TableCell>
                  <TableCell><Chip label={role.is_system ? 'System' : 'Custom'} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(role)}><Edit fontSize="small" /></IconButton>
                    {!role.is_system && <IconButton size="small" color="error" onClick={() => setDeleteConfirm(role.id)}><Delete fontSize="small" /></IconButton>}
                  </TableCell>
                </TableRow>
                <TableRow key={`${role.id}-perms`}>
                  <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedRoleId === role.id ? undefined : 0 }}>
                    <Collapse in={expandedRoleId === role.id} unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                        {permLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
                        ) : (
                          <>
                            <Tabs value={permTab} onChange={(_, v) => setPermTab(v)} sx={{ mb: 2 }}>
                              <Tab label="Object Permissions" />
                              <Tab label="Field Permissions" />
                              <Tab label="Data Scopes" />
                            </Tabs>

                            {permTab === 0 && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                  <Button
                                    size="small" variant="contained" startIcon={<Save />}
                                    onClick={saveObjectPermissions} disabled={permSaving}
                                  >
                                    {permSaving ? <CircularProgress size={16} /> : 'Save'}
                                  </Button>
                                </Box>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>Object</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {objectsList.map((obj) => (
                                      <TableRow key={obj.id}>
                                        <TableCell>{obj.label} <Typography component="span" variant="caption" color="text.secondary">({obj.name})</Typography></TableCell>
                                        <TableCell>
                                          <FormControl size="small" sx={{ minWidth: 160 }}>
                                            <Select
                                              value={objPerms.get(obj.id) ?? 'none'}
                                              onChange={(e) => handleObjPermChange(obj.id, e.target.value as ObjectPermission)}
                                            >
                                              {OBJECT_PERMISSION_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}

                            {permTab === 1 && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                  <Button
                                    size="small" variant="contained" startIcon={<Save />}
                                    onClick={saveFieldPermissions} disabled={permSaving}
                                  >
                                    {permSaving ? <CircularProgress size={16} /> : 'Save'}
                                  </Button>
                                </Box>
                                {objectsList.map((obj) => {
                                  const fields = objectsFieldsMap.get(obj.id) ?? []
                                  if (fields.length === 0) return null
                                  const isExpanded = expandedFieldObjIds.has(obj.id)
                                  return (
                                    <Box key={obj.id} sx={{ mb: 1 }}>
                                      <Paper variant="outlined">
                                        <Box
                                          sx={{ px: 2, py: 1, bgcolor: 'action.hover', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
                                          onClick={() => toggleFieldObj(obj.id)}
                                        >
                                          {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{obj.label}</Typography>
                                          <Chip label={`${fields.length} fields`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                                        </Box>
                                        <Collapse in={isExpanded} unmountOnExit>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {fields.map((field) => (
                                                <TableRow key={field.field_id}>
                                                  <TableCell>{field.label}</TableCell>
                                                  <TableCell><Chip label={field.type} size="small" variant="outlined" /></TableCell>
                                                  <TableCell>
                                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                                      <Select
                                                        value={fieldPerms.get(`${obj.id}:${field.field_id}`) ?? 'read_write'}
                                                        onChange={(e) => handleFieldPermChange(obj.id, field.field_id, e.target.value as FieldPermission)}
                                                      >
                                                        {FIELD_PERMISSION_OPTIONS.map((opt) => (
                                                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                        ))}
                                                      </Select>
                                                    </FormControl>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Collapse>
                                      </Paper>
                                    </Box>
                                  )
                                })}
                              </Box>
                            )}

                            {permTab === 2 && (
                              <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                  <Button
                                    size="small" variant="contained" startIcon={<Save />}
                                    onClick={saveDataScopes} disabled={permSaving}
                                  >
                                    {permSaving ? <CircularProgress size={16} /> : 'Save'}
                                  </Button>
                                </Box>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700 }}>Object</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                                      <TableCell sx={{ fontWeight: 700 }}>Custom Filter</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {objectsList.map((obj) => {
                                      const scope = dataScopes.get(obj.id)
                                      return (
                                        <TableRow key={obj.id}>
                                          <TableCell>{obj.label}</TableCell>
                                          <TableCell>
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                              <Select
                                                value={scope?.scope_type ?? 'all'}
                                                onChange={(e) => handleScopeChange(obj.id, e.target.value as DataScopeType)}
                                              >
                                                {SCOPE_TYPE_OPTIONS.map((opt) => (
                                                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                          </TableCell>
                                          <TableCell>
                                            {scope?.scope_type === 'custom' ? (
                                              <TextField
                                                size="small" fullWidth
                                                placeholder='{"field":"status","op":"eq","value":"active"}'
                                                value={scope.custom_filter}
                                                onChange={(e) => handleCustomFilterChange(obj.id, e.target.value)}
                                              />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      )
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRole ? 'Edit Role' : 'New Role'}</DialogTitle>
        <DialogContent>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} fullWidth size="small" sx={{ mt: 1 }} disabled={!!editRole} />
          <TextField label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} fullWidth size="small" sx={{ mt: 2 }} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Role"
        message="Are you sure you want to delete this role? All associated permissions will be removed."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => handleDelete(deleteConfirm!)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  )
}
